/**
 * WHERE "NOW" FALLS in a list of trips — the one rule, shared by the
 * office's Daily Overview timeline, the dispatch board and the driver's own
 * day.
 *
 * Three screens, two design systems, one reading. What differs between them
 * is only how the line is drawn and where it may appear; whether it appears
 * at all, and between which two trips, is decided here so the three cannot
 * drift into disagreeing about what has already happened.
 *
 * TIMEZONE IS THE WHOLE POINT, and it is a trap worth spelling out. "Is
 * the day on screen the day we are in" (`periodHoldsNow`) is a CALENDAR
 * question and must be asked in Budapest, because that is the day the rows
 * are grouped and labelled in (`formatDate` — Europe/Budapest, never the
 * browser's zone). Ask it with a bare `new Date()` and local midnight and
 * the line appears on the wrong day for anybody whose machine is not on
 * Budapest time — including, silently, a test suite run under `TZ=UTC`,
 * where a naive version looks perfectly right and is wrong in life.
 *
 * The other question — "which trips have already happened" — is not a
 * calendar question at all. It compares two INSTANTS, and an instant is the
 * same instant in every zone, so `nowSplitIndex` does plain arithmetic on
 * epoch milliseconds and needs no zone of its own.
 */
import { formatDate, formatTime } from './timezone';

/** The Budapest calendar day an instant falls on, as 'YYYY-MM-DD'. */
export const budapestDay = (now) => formatDate(now);

/** '14:05' — the marker's label, on the Budapest clock like every other time. */
export const nowTimeLabel = (now) => formatTime(now);

/**
 * Is the period on screen one that CONTAINS this instant?
 *
 * The marker is meaningless on any other day: a red "now" across tomorrow's
 * list, or last Tuesday's, says nothing true about either. `from` and `to`
 * are inclusive Budapest calendar days ('YYYY-MM-DD'), which compare
 * correctly as strings, so a week that holds today somewhere in the middle
 * passes and a week that does not, does not.
 */
export const periodHoldsNow = (from, to, now) => {
  const day = budapestDay(now);
  const start = from ? budapestDay(from) : '';
  const end = to ? budapestDay(to) : '';

  return Boolean(start && end) && start <= day && day <= end;
};

/**
 * Where the line goes in a list of trips already in pickup order: the
 * number of trips at or before `now`, i.e. the index to insert at.
 *
 * `-1` means draw nothing.
 *
 * @param trips  the rows as they are rendered, ascending by pickup time
 * @param now    epoch milliseconds, or anything `new Date()` accepts
 * @param onlyBetween  true for a list that is ONE OF MANY on the screen —
 *   a single driver's group on the dispatch board. A line at the very top
 *   or the very bottom of such a list divides nothing, and twenty of them
 *   down a board would be wallpaper rather than a clock. A screen showing
 *   ONE list of the day (the Daily Overview, the driver's own day) leaves
 *   this false: "everything is still ahead" is worth saying there.
 */
export const nowSplitIndex = (trips, now, { onlyBetween = false } = {}) => {
  const rows = trips || [];

  if (!rows.length) return -1;

  const at = new Date(now).getTime();

  if (!Number.isFinite(at)) return -1;

  const started = (trip) => {
    const pickup = new Date(trip?.date_datetime).getTime();
    // A row with no pickup time cannot be placed either side of a clock;
    // treating it as already gone keeps it above the line rather than
    // letting it push the line to the top of the list.
    return !Number.isFinite(pickup) || pickup <= at;
  };

  const index = rows.findIndex((trip) => !started(trip));
  const split = index === -1 ? rows.length : index;

  return onlyBetween && (split === 0 || split === rows.length) ? -1 : split;
};

/** Trips in pickup order, oldest first, whatever order the list arrived in. Non-mutating. */
export const byPickup = (trips) => [...(trips || [])].sort(
  (a, b) => new Date(a?.date_datetime || 0) - new Date(b?.date_datetime || 0),
);
